import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import { PotatoDiseases, ProcessedAnswers, DiseasesOnPlotResult, QuestionnaireAnswerValue, SoilQuestions } from 'src/app/tga/shared/models/models';

import { selectSoilQuestions } from 'src/app/tga/store/tga.selectors';

import {
  DepthStorendeLaag,
  GrasslandOrGrassSeed,
  MainSoilTillageMethod,
  MaterialState,
  NematodeLaboraties,
  Structuurbederfsource,
  TGACheckboxQuestion,
  TGADateYearQuestion,
  TGADropdownQuestion,
  TGAFreeTextQuestion,
  TgaInputType,
  TGANumberQuestion,
  TGAQuestion,
  TGARadioButtonQuestion,
  TGATableQuestion,
  TGAWarningMessageQuestion,
} from '../../../models/question-models';
import { addQuestionValidators } from '../../question-helpers';
import { SharedSpecificQuestionnaireService } from '../shared/shared-specific-questionnaire.service';

@Injectable({
  providedIn: 'root',
})
export class SpecificSoilQuestionnaireService extends SharedSpecificQuestionnaireService {
  readonly store = inject(Store);

  public static readonly isRentedField = 'IsRentedField';
  public static readonly lastHarvestedYear = 'lastHarvestedYear';
  public static readonly mainSoilTillageMethod = 'mainSoilTillageMethod';
  public static readonly isInLuwte = 'isInLuwte';
  public static readonly hadGrasslandOrGrassSeed = 'hadGrasslandOrGrassSeed';
  public static readonly greenManureCropId = 'greenManureCropId';
  public static readonly diseasesObservedOnThePlot = 'diseasesObservedOnThePlot';
  public static readonly hasSoilVariety = 'hasSoilVariety';
  public static readonly hasSoilscan = 'hasSoilscan';
  public static readonly soilVarietyDifferences = 'soilVarietyDifferences';
  public static readonly IsStuifgevoelig = 'IsStuifgevoelig';
  public static readonly structuurbederfsource = 'hasStructuurbederf';
  public static readonly depthStorendeLaag = 'depthStorendeLaag';
  public static readonly isBeredenUnderWetConditions = 'isBeredenUnderWetConditions';
  public static readonly hasWorkingDrainage = 'hasWorkingDrainage';
  public static readonly hasLaagten = 'hasLaagten';

  // Aardappelmoeheid (AM) analysis
  public static readonly amAnalysisHasAmAnalysis = 'amAnalysisHasAmAnalysis';
  public static readonly amAnalysisLaboratory = 'amAnalysisLaboratory';
  public static readonly amAnalysisAmSampleYear = 'amAnalysisAmSampleYear';
  public static readonly amAnalysisTotalLevendeLarvenEieren = 'amAnalysisTotalLevendeLarvenEieren';
  public static readonly amAnalysisGRostochiensisPercentage = 'amAnalysisGRostochiensisPercentage';
  public static readonly amAnalysisGPallidaPercentage = 'amAnalysisGPallidaPercentage';

  // vrijlevend analysis
  public static readonly vrijlevendAnalysisHasAaltjesAnalysis = 'vrijlevendAnalysisHasAaltjesAnalysis';
  public static readonly vrijlevendAnalysisSampleYear = 'vrijlevendSampleYear';
  public static readonly vrijlevendAnalysisDitylenchusDestructor = 'vrijlevendAnalysisDitylenchusDestructor';
  public static readonly vrijlevendAnalysisDitylenchusDipsaci = 'vrijlevendAnalysisDitylenchusDipsaci';
  public static readonly vrijlevendAnalysisParatrichodorusPachydermus = 'vrijlevendAnalysisParatrichodorusPachydermus';
  public static readonly vrijlevendAnalysisParatrichodorusTeres = 'vrijlevendAnalysisParatrichodorusTeres';
  public static readonly vrijlevendAnalysisTrichodorusPrimitivus = 'vrijlevendAnalysisTrichodorusPrimitivus';
  public static readonly vrijlevendAnalysisTrichodorusSimilis = 'vrijlevendAnalysisTrichodorusSimilis';
  public static readonly vrijlevendAnalysisMeloidogyneChitwoodi = 'vrijlevendAnalysisMeloidogyneChitwoodi';
  public static readonly vrijlevendAnalysisMeloidogyneFallax = 'vrijlevendAnalysisMeloidogyneFallax';
  public static readonly vrijlevendAnalysisMeloidogyneHapla = 'vrijlevendAnalysisMeloidogyneHapla';
  public static readonly vrijlevendAnalysisPratylenchusPenetrans = 'vrijlevendAnalysisPratylenchusPenetrans';

  public static readonly isExpectedInUitgangsmateriaal = 'isExpectedInUitgangsmateriaal';
  public static readonly knolbehandelingMaterial = 'knolbehandelingMaterial';

  // Diseases observed on the plot checkbox options
  private readonly hadAaltjesDamage = 'HadAaltjesDamage';
  private readonly expectedStorageOnCropField = 'ExpectedStorageOnCropField';
  private readonly hasGewoneSchurftPressure = 'HasGewoneSchurftPressure';
  private readonly hasRhizoctoniaPressure = 'HasRhizoctoniaPressure';
  private readonly hadRitnaalden = 'HadRitnaalden';
  private readonly hasSclerotiniaPressure = 'HasSclerotiniaPressure';
  private readonly hadTabaksratelvirus = 'HadTabaksratelvirus';
  private readonly hasZwarteSpikkelPressure = 'HasZwarteSpikkelPressure';
  private readonly hasSnails = 'HasSnails';

  public soilQuestions: TGAQuestion<TgaInputType>[] = [];
  private soilQuestionsState$: Observable<SoilQuestions | null> = of();

  processYesNoAnswer(answer: QuestionnaireAnswerValue): boolean {
    if (typeof answer !== 'string' && !Array.isArray(answer)) {
      return false;
    }

    if (Array.isArray(answer) && typeof answer[0] === 'string') {
      return (answer as string[]).includes('yes');
    }

    return answer === 'yes';
  }

  /**
   * Converts a nullable boolean state value to a question format.
   * Returns 'undefined' for null values, 'yes' for true, 'no' for false.
   */
  private convertNullableBooleanToQuestionValue(value: boolean | null): string | undefined {
    if (value === null) {
      return undefined;
    }
    return value ? 'yes' : 'no';
  }

  /**
   * Converts a nullable boolean state value to a question format with 'unknown' option.
   * Returns 'unknown' for null values, 'yes' for true, 'no' for false.
   */
  private convertNullableBooleanToQuestionValueWithUnknown(value: boolean | null): string {
    if (value === null) {
      return 'unknown';
    }
    return value ? 'yes' : 'no';
  }

  processYesNoUnknownAnswer(answer: QuestionnaireAnswerValue): boolean | null {
    if (typeof answer !== 'string' && !Array.isArray(answer)) {
      return null;
    }

    if (Array.isArray(answer) && typeof answer[0] === 'string') {
      return (answer as string[]).includes('yes');
    }

    if (answer === 'unknown' || answer === '') {
      return null;
    }

    return answer === 'yes';
  }

  processDiseasesOnThePlot(processedAnswers: ProcessedAnswers): DiseasesOnPlotResult {
    const answer = processedAnswers[SpecificSoilQuestionnaireService.diseasesObservedOnThePlot];
    let selectedKeys: string[];

    // Handle checkbox object array format
    if (Array.isArray(answer) && answer.length > 0 && typeof answer[0] === 'object' && 'key' in answer[0]) {
      const checkboxArray = answer as { key: string; selected: boolean }[];
      selectedKeys = checkboxArray
        .filter((item) => item.selected === true)
        .map((item) => item.key);
    } else if (Array.isArray(answer) && answer.length > 0 && typeof answer[0] === 'string') {
      // Handle string array format (backward compatibility)
      selectedKeys = answer as string[];
    } else {
      // Handle empty or invalid data
      selectedKeys = [];
    }

    return {
      hadAaltjesDamage: selectedKeys.includes(this.hadAaltjesDamage),
      expectedStorageOnCropField: selectedKeys.includes(this.expectedStorageOnCropField),
      hasGewoneSchurftPressure: selectedKeys.includes(this.hasGewoneSchurftPressure),
      hasRhizoctoniaPressure: selectedKeys.includes(this.hasRhizoctoniaPressure),
      hadRitnaalden: selectedKeys.includes(this.hadRitnaalden),
      hasSclerotiniaPressure: selectedKeys.includes(this.hasSclerotiniaPressure),
      hadTabaksratelvirus: selectedKeys.includes(this.hadTabaksratelvirus),
      hasZwarteSpikkelPressure: selectedKeys.includes(this.hasZwarteSpikkelPressure),
      hasSnails: selectedKeys.includes(this.hasSnails),
    };
  }

  processGreenManureCropId(answer: QuestionnaireAnswerValue): number | null {
    if (answer === 'None') {
      return null;
    }

    return Number(answer);
  }

  processDateYearAnswer(answer: Date | null): number | null {
    return answer ? answer.getFullYear() : null;
  }

  processIsExpectedInUitgangsmateriaal(answer: QuestionnaireAnswerValue | { key: string; selected: boolean }[]): {
    isRhizoctoniaExpected: boolean;
    isCropVarietyRhizoctoniaSensitive: boolean;
    hasZilverschurftdrukInUitgangsmateriaal: boolean;
    isFusariumExpected: boolean;
  } {
    if (!Array.isArray(answer)) {
      return {
        isRhizoctoniaExpected: false,
        isCropVarietyRhizoctoniaSensitive: false,
        hasZilverschurftdrukInUitgangsmateriaal: false,
        isFusariumExpected: false,
      }
    }

    // Handle string array case
    if (answer.length > 0 && typeof answer[0] === 'string') {
      const stringArray = answer as string[];
      return {
        isRhizoctoniaExpected: stringArray.includes('Rhizoctonia'),
        isCropVarietyRhizoctoniaSensitive: stringArray.includes('Rhizoctonia'),
        hasZilverschurftdrukInUitgangsmateriaal: stringArray.includes('Zilverschurft'),
        isFusariumExpected: stringArray.includes('Fusarium'),
      };
    }

    // Handle key-value object array case
    const keyValueArray = answer as { key: string; selected: boolean }[];
    const rhizoctoniaItem = keyValueArray.find((item) => item.key === 'Rhizoctonia');
    const zilverschurftItem = keyValueArray.find((item) => item.key === 'Zilverschurft');
    const fusariumItem = keyValueArray.find((item) => item.key === 'Fusarium');

    return {
      isRhizoctoniaExpected: rhizoctoniaItem?.selected ?? false,
      isCropVarietyRhizoctoniaSensitive: rhizoctoniaItem?.selected ?? false,
      hasZilverschurftdrukInUitgangsmateriaal: zilverschurftItem?.selected ?? false,
      isFusariumExpected: fusariumItem?.selected ?? false,
    };
  }

  initializeSoilQuestions(greenManureCrops: { key: string; value: string }[]): void {
    this.getQuestions(greenManureCrops).subscribe((questions) => {
      this.soilQuestions = questions;
    });
  }

  updateAmAnalysisPercentageState(questions: TGAQuestion<TgaInputType>[], formGroup: FormGroup): void {
    const shouldDisablePercentages = this.shouldDisableAmAnalysisPercentages(formGroup);
    const percentageQuestionKeys = [
      SpecificSoilQuestionnaireService.amAnalysisGRostochiensisPercentage,
      SpecificSoilQuestionnaireService.amAnalysisGPallidaPercentage,
    ];

    for (const questionKey of percentageQuestionKeys) {
      const question = this.findQuestionByKey(questions, questionKey);
      const control = formGroup.get(questionKey);

      if (!question || !control) {
        continue;
      }

      if (!question.isVisible) {
        continue;
      }

      question.required = !shouldDisablePercentages;
      question.isDisabled = shouldDisablePercentages;

      if (shouldDisablePercentages) {
        control.setValue(null, { emitEvent: false });
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }

      control.clearValidators();
      addQuestionValidators(question, control);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  initValues(): TGAQuestion<TgaInputType>[] {
    this.soilQuestionsState$ = this.store.select(selectSoilQuestions);
    this.soilQuestionsState$.subscribe((state) => {
      if (state) {
        for (const question of this.soilQuestions) {
          switch (question.key) {
            case SpecificSoilQuestionnaireService.isRentedField:
              question.value = state.isRentedField ? 'yes' : 'no';
              break;
            case SpecificSoilQuestionnaireService.lastHarvestedYear:
              question.value = state.lastHarvestedYear ?? undefined;
              break;
            case SpecificSoilQuestionnaireService.mainSoilTillageMethod: {
              question.value = state.mainSoilTillageMethod ? MainSoilTillageMethod[state.mainSoilTillageMethod].toString() : undefined;
              break;
            }
            case SpecificSoilQuestionnaireService.isInLuwte:
              question.value = this.convertNullableBooleanToQuestionValue(state.isInLuwte);
              break;
            case SpecificSoilQuestionnaireService.hadGrasslandOrGrassSeed:
              question.value = state.hadGrasslandOrGrassSeed ? GrasslandOrGrassSeed[state.hadGrasslandOrGrassSeed].toString() : undefined;
              break;
            case SpecificSoilQuestionnaireService.greenManureCropId:
              question.value = state.greenManureCropId?.toString() || 'None';
              break;
            case SpecificSoilQuestionnaireService.diseasesObservedOnThePlot: {
              const diseases = [
                { key: this.hadAaltjesDamage, selected: state.hadAaltjesDamage ?? false },
                { key: this.expectedStorageOnCropField, selected: state.expectedStorageOnCropField ?? false },
                { key: this.hasGewoneSchurftPressure, selected: state.hasGewoneSchurftPressure ?? false },
                { key: this.hasRhizoctoniaPressure, selected: state.hasRhizoctoniaPressure ?? false },
                { key: this.hadRitnaalden, selected: state.hadRitnaalden ?? false },
                { key: this.hasSclerotiniaPressure, selected: state.hasSclerotiniaPressure ?? false },
                { key: this.hadTabaksratelvirus, selected: state.hadTabaksratelvirus ?? false },
                { key: this.hasZwarteSpikkelPressure, selected: state.hasZwarteSpikkelPressure ?? false },
                { key: this.hasSnails, selected: state.hasSnails ?? false },
              ];
              question.value = diseases;
              break;
            }
            case SpecificSoilQuestionnaireService.hasSoilVariety:
              question.value = this.convertNullableBooleanToQuestionValueWithUnknown(state.hasSoilVariety);
              break;
            case SpecificSoilQuestionnaireService.soilVarietyDifferences:
              question.value = state.soilVarietyDifferences ?? undefined;
              break;
            case SpecificSoilQuestionnaireService.hasSoilscan:
              question.value = this.convertNullableBooleanToQuestionValue(state.hasSoilScan);
              break;
            case SpecificSoilQuestionnaireService.IsStuifgevoelig:
              question.value = this.convertNullableBooleanToQuestionValue(state.isStuifgevoelig);
              break;
            case SpecificSoilQuestionnaireService.structuurbederfsource:
              question.value = state.structuurbederfSource ? Structuurbederfsource[state.structuurbederfSource].toString() : undefined;
              break;
            case SpecificSoilQuestionnaireService.depthStorendeLaag:
              question.value = state.depthStorendeLaag ? DepthStorendeLaag[state.depthStorendeLaag].toString() : undefined;
              break;
            case SpecificSoilQuestionnaireService.hasWorkingDrainage:
              question.value = this.convertNullableBooleanToQuestionValue(state.hasWorkingDrainage);
              break;
            case SpecificSoilQuestionnaireService.hasLaagten:
              question.value = this.convertNullableBooleanToQuestionValue(state.hasLaagten);
              break;
            case SpecificSoilQuestionnaireService.isBeredenUnderWetConditions:
              question.value = this.convertNullableBooleanToQuestionValue(state.isBeredenUnderWetConditions);
              break;
            case SpecificSoilQuestionnaireService.amAnalysisHasAmAnalysis:
              question.value = this.convertNullableBooleanToQuestionValue(state.amAnalysisHasAmAnalysis);
              break;
            case SpecificSoilQuestionnaireService.vrijlevendAnalysisHasAaltjesAnalysis:
              question.value = this.convertNullableBooleanToQuestionValue(state.vrijlevendAnalysisHasAaltjesAnalysis);
              break;
            case SpecificSoilQuestionnaireService.isExpectedInUitgangsmateriaal: {
              const expectedItems = [
                { key: 'Rhizoctonia', selected: state.isRhizoctoniaExpected ?? false },
                { key: 'Zilverschurft', selected: state.hasZilverschurftdrukInUitgangsmateriaal ?? false },
                { key: 'Fusarium', selected: state.isFusariumExpected ?? false },
              ];
              question.value = expectedItems;
              break;
            }
            case SpecificSoilQuestionnaireService.knolbehandelingMaterial:
              question.value = state.knolbehandelingMaterial ? MaterialState[state.knolbehandelingMaterial].toString() : undefined;
              break;
          }

          // Handle TGATableQuestion subQuestions
          if (this.isTableQuestion(question)) {
            for (const subQuestion of question.subQuestions) {
              this.updateSubQuestionValue(subQuestion, state);
            }
          }
        }
      }
    });

    return this.soilQuestions;
  }

  private updateSubQuestionValue(subQuestion: TGAQuestion<TgaInputType>, state: SoilQuestions): void {
    switch (subQuestion.key) {
      // AM Analysis subQuestions
      case SpecificSoilQuestionnaireService.amAnalysisAmSampleYear:
        subQuestion.value = state.amAnalysisAmSampleYear ?? undefined;
        break;
      case SpecificSoilQuestionnaireService.amAnalysisLaboratory:
        subQuestion.value = state.amAnalysisLaboratory ? NematodeLaboraties[state.amAnalysisLaboratory].toString() : undefined;
        break;
      case SpecificSoilQuestionnaireService.amAnalysisTotalLevendeLarvenEieren:
        subQuestion.value = state.amAnalysisTotalLevendeLarvenEieren ?? undefined;
        break;
      case SpecificSoilQuestionnaireService.amAnalysisGRostochiensisPercentage:
        subQuestion.value = state.amAnalysisGRostochiensisPercentage ?? undefined;
        break;
      case SpecificSoilQuestionnaireService.amAnalysisGPallidaPercentage:
        subQuestion.value = state.amAnalysisGPallidaPercentage ?? undefined;
        break;

      // Vrijlevende Analysis subQuestions
      case SpecificSoilQuestionnaireService.vrijlevendAnalysisSampleYear:
        subQuestion.value = state.vrijlevendAnalysisSampleYear ?? undefined;
        break;
      case SpecificSoilQuestionnaireService.vrijlevendAnalysisDitylenchusDestructor:
        subQuestion.value = state.vrijlevendAnalysisDitylenchusDestructor ?? undefined;
        break;
      case SpecificSoilQuestionnaireService.vrijlevendAnalysisDitylenchusDipsaci:
        subQuestion.value = state.vrijlevendAnalysisDitylenchusDipsaci ?? undefined;
        break;
      case SpecificSoilQuestionnaireService.vrijlevendAnalysisParatrichodorusPachydermus:
        subQuestion.value = state.vrijlevendAnalysisParatrichodorusPachydermus ?? undefined;
        break;
      case SpecificSoilQuestionnaireService.vrijlevendAnalysisParatrichodorusTeres:
        subQuestion.value = state.vrijlevendAnalysisParatrichodorusTeres ?? undefined;
        break;
      case SpecificSoilQuestionnaireService.vrijlevendAnalysisTrichodorusPrimitivus:
        subQuestion.value = state.vrijlevendAnalysisTrichodorusPrimitivus ?? undefined;
        break;
      case SpecificSoilQuestionnaireService.vrijlevendAnalysisTrichodorusSimilis:
        subQuestion.value = state.vrijlevendAnalysisTrichodorusSimilis ?? undefined;
        break;
      case SpecificSoilQuestionnaireService.vrijlevendAnalysisMeloidogyneChitwoodi:
        subQuestion.value = state.vrijlevendAnalysisMeloidogyneChitwoodi ?? undefined;
        break;
      case SpecificSoilQuestionnaireService.vrijlevendAnalysisMeloidogyneFallax:
        subQuestion.value = state.vrijlevendAnalysisMeloidogyneFallax ?? undefined;
        break;
      case SpecificSoilQuestionnaireService.vrijlevendAnalysisMeloidogyneHapla:
        subQuestion.value = state.vrijlevendAnalysisMeloidogyneHapla ?? undefined;
        break;
      case SpecificSoilQuestionnaireService.vrijlevendAnalysisPratylenchusPenetrans:
        subQuestion.value = state.vrijlevendAnalysisPratylenchusPenetrans ?? undefined;
        break;

      // Warning message questions don't need value updates
      default:
        // No action needed for other question types like TGAWarningMessageQuestion
        break;
    }
  }

  private shouldDisableAmAnalysisPercentages(formGroup: FormGroup): boolean {
    return formGroup.get(SpecificSoilQuestionnaireService.amAnalysisHasAmAnalysis)?.value === 'yes' &&
      this.parseNumberValue(formGroup.get(SpecificSoilQuestionnaireService.amAnalysisTotalLevendeLarvenEieren)?.value) === 0;
  }

  private parseNumberValue(value: unknown): number | null {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const parsedValue = Number(value);
      return Number.isNaN(parsedValue) ? null : parsedValue;
    }

    return null;
  }

  private findQuestionByKey(questions: TGAQuestion<TgaInputType>[], key: string): TGAQuestion<TgaInputType> | undefined {
    for (const question of questions) {
      if (question.key === key) {
        return question;
      }

      if (this.isTableQuestion(question)) {
        const subQuestion = question.subQuestions.find((currentQuestion) => currentQuestion.key === key);
        if (subQuestion) {
          return subQuestion;
        }
      }
    }

    return undefined;
  }

  private isTableQuestion(question: TGAQuestion<TgaInputType>): question is TGATableQuestion {
    const subQuestions = Reflect.get(question, 'subQuestions');
    return question.controlType === TGATableQuestion.tableControlType && Array.isArray(subQuestions);
  }

  getQuestions(greenManureCrops: { key: string; value: string }[]): Observable<TGAQuestion<TgaInputType>[]> {
    const currentYear = new Date().getFullYear();
    // Use UTC-based Jan 1 dates so range comparisons don't suffer timezone shifts
    let today = new Date(Date.UTC(currentYear, 0, 1));
    let fifteenYearsAgo = new Date(Date.UTC(currentYear - 15, 0, 1));


    return of([
      new TGARadioButtonQuestion({
        key: SpecificSoilQuestionnaireService.isRentedField,
        label: 'TGA.Questionnaire.SoilQuestions.OwnOrRentalLand.Question',
        options: [
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.OwnOrRentalLand.Own' },
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.OwnOrRentalLand.Rental' },
        ],
        required: true,
      }),

      new TGADateYearQuestion({
        key: SpecificSoilQuestionnaireService.lastHarvestedYear,
        label: 'TGA.Questionnaire.SoilQuestions.LastHarvestedYear.Question',
        placeholder: 'TGA.Questionnaire.SoilQuestions.LastHarvestedYear.Placeholder',
        value: undefined,
        range: { min: fifteenYearsAgo, max: today },
        required: false,
      }),

      new TGADropdownQuestion({
        options: {
          key: SpecificSoilQuestionnaireService.mainSoilTillageMethod,
          label: 'TGA.Questionnaire.SoilQuestions.MainTillage.Question',
          placeholder: 'TGA.Questionnaire.SoilQuestions.MainTillage.Placeholder',
          order: 1,
          options: [
            {
              key: MainSoilTillageMethod.TraditioneelPloegBelow.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.TraditionalPlowLessThan25Cm',
            },
            {
              key: MainSoilTillageMethod.TraditioneelPloegAbove.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.TraditionalPlowMoreThan25Cm',
            },
            {
              key: MainSoilTillageMethod.SpittenBelow.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.SpadingLessThan25Cm',
            },
            {
              key: MainSoilTillageMethod.SpittenAbove.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.SpadingMoreThan25Cm',
            },
            {
              key: MainSoilTillageMethod.BovenoverPloegAbove.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.PloughingOver',
            },
            {
              key: MainSoilTillageMethod.BovenoverPloegBelow.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.ShallowTopPloughing',
            },
            {
              key: MainSoilTillageMethod.NietKerendeGrondbewerking.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.NonInversionTillage',
            },
            {
              key: MainSoilTillageMethod.GeenGrondbewerking.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.NoTillage',
            },
          ],
          required: true,
        },
      }),

      new TGARadioButtonQuestion({
        key: SpecificSoilQuestionnaireService.isInLuwte,
        label: 'TGA.Questionnaire.SoilQuestions.PlotInShelteredLocation.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.PlotInShelteredLocation.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.PlotInShelteredLocation.No' },
        ],
        required: true,
      }),

      new TGADropdownQuestion({
        options: {
          key: SpecificSoilQuestionnaireService.hadGrasslandOrGrassSeed,
          label: 'TGA.Questionnaire.SoilQuestions.HadGrasslandOrGrassSeed.Question',
          placeholder: 'TGA.Questionnaire.SoilQuestions.HadGrasslandOrGrassSeed.Placeholder',
          order: 1,
          options: [
            {
              key: GrasslandOrGrassSeed.YesLast5Years.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.HadGrasslandOrGrassSeed.Options.YesInTheLast5Years',
            },
            {
              key: GrasslandOrGrassSeed.Yes5To10Years.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.HadGrasslandOrGrassSeed.Options.Yes5To10YearsAgo',
            },
            {
              key: GrasslandOrGrassSeed.YesMoreThan10Years.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.HadGrasslandOrGrassSeed.Options.YesMoreThan10YearsAgo',
            },
            {
              key: GrasslandOrGrassSeed.No.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.HadGrasslandOrGrassSeed.Options.No',
            },
          ],
          required: true,
        },
      }),

      new TGADropdownQuestion({
        options: {
          key: SpecificSoilQuestionnaireService.greenManureCropId,
          label: 'TGA.Questionnaire.SoilQuestions.GreenManureCrop.Question',
          placeholder: 'TGA.Questionnaire.SoilQuestions.GreenManureCrop.Placeholder',
          order: 1,
          options: [
            {
              key: 'None',
              value: 'TGA.Questionnaire.SoilQuestions.GreenManureCrop.Options.None',
            },
            ...greenManureCrops,
          ],
          required: true,
        },
      }),

      new TGACheckboxQuestion({
        key: SpecificSoilQuestionnaireService.diseasesObservedOnThePlot,
        label: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Question',
        options: [
          {
            key: this.hadAaltjesDamage,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HadAaltjesDamage',
          },
          {
            key: this.expectedStorageOnCropField,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.ExpectedStorageOnCropField',
          },
          {
            key: this.hasGewoneSchurftPressure,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HasGewoneSchurftPressure',
          },
          {
            key: this.hasRhizoctoniaPressure,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HasRhizoctoniaPressure',
          },
          {
            key: this.hadRitnaalden,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HasRitnaalden',
          },
          {
            key: this.hasSclerotiniaPressure,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HasSclerotiniaPressure',
          },
          {
            key: this.hadTabaksratelvirus,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HadTabaksratelvirus',
          },
          {
            key: this.hasZwarteSpikkelPressure,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HasZwarteSpikkelPressure',
          },
          {
            key: this.hasSnails,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HasSnails',
          },
        ],
        required: true,
      }),

      new TGARadioButtonQuestion({
        key: SpecificSoilQuestionnaireService.hasSoilVariety,
        label: 'TGA.Questionnaire.SoilQuestions.SoilVariation.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.SoilVariation.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.SoilVariation.No' },
          { key: 'unknown', value: 'TGA.Questionnaire.SoilQuestions.SoilVariation.Unknown' },
        ],
        required: true,
      }),

      new TGAFreeTextQuestion({
        key: SpecificSoilQuestionnaireService.soilVarietyDifferences,
        label: 'TGA.Questionnaire.SoilQuestions.SoilVariationDifferences',
        value: undefined,
        dependsOnQuestion: {
          questionKey: SpecificSoilQuestionnaireService.hasSoilVariety,
          questionVisibleForValues: ['yes'],
        },
        required: false,
      }),

      new TGARadioButtonQuestion({
        key: SpecificSoilQuestionnaireService.hasSoilscan,
        label: 'TGA.Questionnaire.SoilQuestions.WasASoilScanCarriedOut.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.WasASoilScanCarriedOut.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.WasASoilScanCarriedOut.No' },
        ],
        dependsOnQuestion: {
          questionKey: SpecificSoilQuestionnaireService.hasSoilVariety,
          questionVisibleForValues: ['yes'],
        },
        required: false,
      }),

      new TGARadioButtonQuestion({
        key: SpecificSoilQuestionnaireService.IsStuifgevoelig,
        label: 'TGA.Questionnaire.SoilQuestions.IsThePlotSensitiveToDrift.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.IsThePlotSensitiveToDrift.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.IsThePlotSensitiveToDrift.No' },
        ],
        required: true,
      }),

      new TGADropdownQuestion({
        options: {
          key: SpecificSoilQuestionnaireService.structuurbederfsource,
          label: 'TGA.Questionnaire.SoilQuestions.Structuurbederfsource.Question',
          placeholder: 'TGA.Questionnaire.SoilQuestions.Structuurbederfsource.Placeholder',
          order: 1,
          options: [
            {
              key: Structuurbederfsource.No.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.Structuurbederfsource.Options.No',
            },
            {
              key: Structuurbederfsource.Bewerkbaarheid.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.Structuurbederfsource.Options.YesEdited',
            },
            {
              key: Structuurbederfsource.Doorlatendheid.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.Structuurbederfsource.Options.YesPermeability',
            },
            {
              key: Structuurbederfsource.StorendeLaag.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.Structuurbederfsource.Options.YesDisturbingLayers',
            },
          ],
          required: true,
        },
      }),

      new TGADropdownQuestion({
        options: {
          key: SpecificSoilQuestionnaireService.depthStorendeLaag,
          label: 'TGA.Questionnaire.SoilQuestions.AtWhatDepthAreTheDisturbingLayersVisible.Question',
          placeholder: 'TGA.Questionnaire.SoilQuestions.AtWhatDepthAreTheDisturbingLayersVisible.Placeholder',
          order: 1,
          options: [
            {
              key: DepthStorendeLaag.In0to10.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.AtWhatDepthAreTheDisturbingLayersVisible.Options.ZeroToTenCm',
            },
            {
              key: DepthStorendeLaag.In10to25.toString(),
              value:
                'TGA.Questionnaire.SoilQuestions.AtWhatDepthAreTheDisturbingLayersVisible.Options.TenToTwentyFiveCm',
            },
            {
              key: DepthStorendeLaag.In25to35.toString(),
              value:
                'TGA.Questionnaire.SoilQuestions.AtWhatDepthAreTheDisturbingLayersVisible.Options.TwentyFiveToThirtyFiveCm',
            },
            {
              key: DepthStorendeLaag.In35orMore.toString(),
              value:
                'TGA.Questionnaire.SoilQuestions.AtWhatDepthAreTheDisturbingLayersVisible.Options.MoreThanThirtyFiveCm',
            },
          ],
          dependsOnQuestion: {
            questionKey: SpecificSoilQuestionnaireService.structuurbederfsource,
            questionVisibleForValues: [Structuurbederfsource.StorendeLaag.toString()],
          },
          required: false,
        },
      }),

      new TGARadioButtonQuestion({
        key: SpecificSoilQuestionnaireService.hasWorkingDrainage,
        label: 'TGA.Questionnaire.SoilQuestions.IsTheDrainageContinuing.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.IsTheDrainageContinuing.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.IsTheDrainageContinuing.No' },
          { key: 'unknown', value: 'TGA.Questionnaire.SoilQuestions.IsTheDrainageContinuing.Unknown' },
        ],
        dependsOnQuestion: {
          questionKey: SpecificSoilQuestionnaireService.structuurbederfsource,
          questionVisibleForValues: ['YesPermeability'],
        },
        required: false,
      }),

      new TGARadioButtonQuestion({
        key: SpecificSoilQuestionnaireService.hasLaagten,
        label: 'TGA.Questionnaire.SoilQuestions.AnyDepressionsPresent.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.AnyDepressionsPresent.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.AnyDepressionsPresent.No' },
          { key: 'unknown', value: 'TGA.Questionnaire.SoilQuestions.AnyDepressionsPresent.Unknown' },
        ],
        dependsOnQuestion: {
          questionKey: SpecificSoilQuestionnaireService.structuurbederfsource,
          questionVisibleForValues: ['YesPermeability'],
        },
        required: false,
      }),

      new TGARadioButtonQuestion({
        key: SpecificSoilQuestionnaireService.isBeredenUnderWetConditions,
        label: 'TGA.Questionnaire.SoilQuestions.PlotDrivenInWetConditions.Question',
        placeholder: 'TGA.Questionnaire.SoilQuestions.PlotDrivenInWetConditions.Placeholder',
        order: 1,
        options: [
          {
            key: 'yes',
            value: 'TGA.Questionnaire.SoilQuestions.PlotDrivenInWetConditions.Options.Yes',
          },
          {
            key: 'no',
            value: 'TGA.Questionnaire.SoilQuestions.PlotDrivenInWetConditions.Options.No',
          },
          {
            key: 'unknown',
            value: 'TGA.Questionnaire.SoilQuestions.PlotDrivenInWetConditions.Options.Unknown',
          },
        ],
        required: true,
      }),

      new TGARadioButtonQuestion({
        key: SpecificSoilQuestionnaireService.amAnalysisHasAmAnalysis,
        label: 'TGA.Questionnaire.SoilQuestions.HasAMAnalysis.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.HasAMAnalysis.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.HasAMAnalysis.No' },
        ],
        required: true,
      }),

      new TGATableQuestion({
        key: 'amAnalysisTable',
        label: 'TGA.Questionnaire.SoilQuestions.AmAnalysisTable.Title',
        dependsOnQuestion: {
          questionKey: SpecificSoilQuestionnaireService.amAnalysisHasAmAnalysis,
          questionVisibleForValues: ['yes'],
        },
        subQuestions: [
          new TGADateYearQuestion({
            key: SpecificSoilQuestionnaireService.amAnalysisAmSampleYear,
            label: 'TGA.Questionnaire.SoilQuestions.AmAnalysisTable.SampleYear',
            required: true,
            placeholder: 'TGA.Questionnaire.SoilQuestions.AmAnalysisTable.SampleYearPlaceholder',
            range: { min: fifteenYearsAgo, max: today },
            dependsOnQuestion: {
              questionKey: SpecificSoilQuestionnaireService.amAnalysisHasAmAnalysis,
              questionVisibleForValues: ['yes'],
            },
          }),
          new TGADropdownQuestion({
            options: {
              key: SpecificSoilQuestionnaireService.amAnalysisLaboratory,
              label: 'TGA.Questionnaire.SoilQuestions.AmAnalysisTable.Laboratory',
              placeholder: 'TGA.Questionnaire.SoilQuestions.AmAnalysisTable.LaboratoryPlaceholder',
              options: [
                {
                  key: NematodeLaboraties.Eurofins.toString(),
                  value: 'Eurofins',
                },
                {
                  key: NematodeLaboraties.HLB.toString(),
                  value: 'HLB',
                },
                {
                  key: NematodeLaboraties.NAK.toString(),
                  value: 'NAK',
                },
                {
                  key: NematodeLaboraties.Nemacontrol.toString(),
                  value: 'Nemacontrol',
                },
                {
                  key: NematodeLaboraties.Roba.toString(),
                  value: 'Roba',
                },
                {
                  key: NematodeLaboraties.Other.toString(),
                  value: 'other',
                },
              ],
              required: true,
              dependsOnQuestion: {
                questionKey: SpecificSoilQuestionnaireService.amAnalysisHasAmAnalysis,
                questionVisibleForValues: ['yes'],
              },
            },
          }),
          new TGANumberQuestion({
            key: SpecificSoilQuestionnaireService.amAnalysisTotalLevendeLarvenEieren,
            label: 'TGA.Questionnaire.SoilQuestions.AmAnalysisTable.TotalLevendeLarvenEieren',
            required: true,
            placeholder: 'TGA.Questionnaire.SoilQuestions.AmAnalysisTable.TotalLevendeLarvenEierenPlaceholder',
            range: { min: 0, max: 60000 },
            dependsOnQuestion: {
              questionKey: SpecificSoilQuestionnaireService.amAnalysisHasAmAnalysis,
              questionVisibleForValues: ['yes'],
            },
          }),

          new TGANumberQuestion({
            key: SpecificSoilQuestionnaireService.amAnalysisGRostochiensisPercentage,
            label: 'TGA.Questionnaire.SoilQuestions.AmAnalysisTable.GRostochiensisPercentage',
            required: true,
            placeholder: 'TGA.Questionnaire.SoilQuestions.AmAnalysisTable.GRostochiensisPercentagePlaceholder',
            range: { min: 0, max: 100 },
            dependsOnQuestion: {
              questionKey: SpecificSoilQuestionnaireService.amAnalysisHasAmAnalysis,
              questionVisibleForValues: ['yes'],
            },
          }),

          new TGANumberQuestion({
            key: SpecificSoilQuestionnaireService.amAnalysisGPallidaPercentage,
            label: 'TGA.Questionnaire.SoilQuestions.AmAnalysisTable.GPallidaPercentage',
            required: true,
            placeholder: 'TGA.Questionnaire.SoilQuestions.AmAnalysisTable.GPallidaPercentagePlaceholder',
            range: { min: 0, max: 100 },
            dependsOnQuestion: {
              questionKey: SpecificSoilQuestionnaireService.amAnalysisHasAmAnalysis,
              questionVisibleForValues: ['yes'],
            },
          }),
        ],
      }),

      new TGARadioButtonQuestion({
        key: SpecificSoilQuestionnaireService.vrijlevendAnalysisHasAaltjesAnalysis,
        label: 'TGA.Questionnaire.SoilQuestions.HasAaltjesAnalysis.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.HasAaltjesAnalysis.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.HasAaltjesAnalysis.No' },
        ],
        required: true,
      }),

      new TGATableQuestion({
        key: 'vrijlevendeAaltjesAnalysisTable',
        label: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.Title',
        dependsOnQuestion: {
          questionKey: SpecificSoilQuestionnaireService.vrijlevendAnalysisHasAaltjesAnalysis,
          questionVisibleForValues: ['yes'],
        },
        subQuestions: [
          new TGADateYearQuestion({
            key: SpecificSoilQuestionnaireService.vrijlevendAnalysisSampleYear,
            label: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.SampleYear',
            required: true,
            placeholder: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.SampleYearPlaceholder',
            range: { min: fifteenYearsAgo, max: today },
            dependsOnQuestion: {
              questionKey: SpecificSoilQuestionnaireService.vrijlevendAnalysisHasAaltjesAnalysis,
              questionVisibleForValues: ['yes'],
            },
          }),

          new TGAWarningMessageQuestion
            ({
              key: 'vrijlevendeAnalysisAantalAaltjes',
              label: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.AantalAaltjesHeader',
              showAsRegularText: true,
              placeholder: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.AantalAaltjesSubheader',
            }),

          new TGANumberQuestion({
            key: SpecificSoilQuestionnaireService.vrijlevendAnalysisDitylenchusDestructor,
            label: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.DitylenchusDestructor',
            required: false,
            placeholder: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.ValuePlaceholder',
            range: { min: 0, max: 10000 },
          }),

          new TGANumberQuestion({
            key: SpecificSoilQuestionnaireService.vrijlevendAnalysisDitylenchusDipsaci,
            label: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.DitylenchusDipsaci',
            required: false,
            placeholder: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.ValuePlaceholder',
            range: { min: 0, max: 10000 },
          }),

          new TGANumberQuestion({
            key: SpecificSoilQuestionnaireService.vrijlevendAnalysisParatrichodorusPachydermus,
            label: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.ParatrichodorusPachydermus',
            required: false,
            placeholder: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.ValuePlaceholder',
            range: { min: 0, max: 10000 },
          }),

          new TGANumberQuestion({
            key: SpecificSoilQuestionnaireService.vrijlevendAnalysisParatrichodorusTeres,
            label: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.ParatrichodorusTeres',
            required: false,
            placeholder: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.ValuePlaceholder',
            range: { min: 0, max: 10000 },
          }),

          new TGANumberQuestion({
            key: SpecificSoilQuestionnaireService.vrijlevendAnalysisTrichodorusPrimitivus,
            label: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.TrichodorusPrimitivus',
            required: false,
            placeholder: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.ValuePlaceholder',
            range: { min: 0, max: 10000 },
          }),

          new TGANumberQuestion({
            key: SpecificSoilQuestionnaireService.vrijlevendAnalysisTrichodorusSimilis,
            label: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.TrichodorusSimilis',
            required: false,
            placeholder: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.ValuePlaceholder',
            range: { min: 0, max: 10000 },
          }),

          new TGANumberQuestion({
            key: SpecificSoilQuestionnaireService.vrijlevendAnalysisMeloidogyneChitwoodi,
            label: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.MeloidogyneChitwoodi',
            required: false,
            placeholder: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.ValuePlaceholder',
            range: { min: 0, max: 10000 },
          }),

          new TGANumberQuestion({
            key: SpecificSoilQuestionnaireService.vrijlevendAnalysisMeloidogyneFallax,
            label: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.MeloidogyneFallax',
            required: false,
            placeholder: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.ValuePlaceholder',
            range: { min: 0, max: 10000 },
          }),

          new TGANumberQuestion({
            key: SpecificSoilQuestionnaireService.vrijlevendAnalysisMeloidogyneHapla,
            label: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.MeloidogyneHapla',
            required: false,
            placeholder: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.ValuePlaceholder',
            range: { min: 0, max: 10000 },
          }),

          new TGANumberQuestion({
            key: SpecificSoilQuestionnaireService.vrijlevendAnalysisPratylenchusPenetrans,
            label: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.PratylenchusPenetrans',
            required: false,
            placeholder: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.ValuePlaceholder',
            range: { min: 0, max: 10000 },
          }),

          new TGAWarningMessageQuestion
            ({
              key: 'vrijlevendeAnalysisWarningMessage',
              label: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.CysteaaltjesHeader',
              placeholder: 'TGA.Questionnaire.SoilQuestions.VrijlevendeAaltjesAnalysisTable.CysteaaltjesMessage',
            }),
        ],
      }),

      new TGACheckboxQuestion({
        key: SpecificSoilQuestionnaireService.isExpectedInUitgangsmateriaal,
        label: 'TGA.Questionnaire.SoilQuestions.IsRhizoctoniaExpected.Question',
        options: [
          {
            key: PotatoDiseases.Rhizoctonia,
            value: PotatoDiseases.Rhizoctonia.toString(),
          },
          {
            key: PotatoDiseases.Zilverschurft,
            value: PotatoDiseases.Zilverschurft.toString(),
          },
          {
            key: PotatoDiseases.Fusarium,
            value: PotatoDiseases.Fusarium.toString(),
          },
        ],
        required: true,
        hasInfoIcon: true,
      }),
      new TGARadioButtonQuestion({
        key: SpecificSoilQuestionnaireService.knolbehandelingMaterial,
        label: 'TGA.Questionnaire.SoilQuestions.KnolbehandelingMaterial.Question',
        order: 3,
        options: [
          {
            key: MaterialState.Liquid.toString(),
            value: 'TGA.Questionnaire.SoilQuestions.KnolbehandelingMaterial.Liquid',
          },
          {
            key: MaterialState.Solid.toString(),
            value: 'TGA.Questionnaire.SoilQuestions.KnolbehandelingMaterial.Solid',
          },
          {
            key: MaterialState.LiquidAndSolid.toString(),
            value: 'TGA.Questionnaire.SoilQuestions.KnolbehandelingMaterial.Both',
          },
        ],
        required: true,
      }),
    ]);
  }
}
